import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardContent,
  CardActionArea,
  Grid,
  Modal,
  IconButton,
} from '@mui/material';
import { Close, RadioButtonUnchecked, RadioButtonChecked } from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface InstructionType {
  id: number;
  name: string;
  description: string;
  sampleValue: string;
  referenceImage: string;
  sortOrder: number;
}

interface SpecialInstructionsModalProps {
  open: boolean;
  onClose: () => void;
  trendReportId: string;
  existingInstructionTypeIds: number[];
  onSave: () => void;
}

export const SpecialInstructionsModal: React.FC<SpecialInstructionsModalProps> = ({
  open,
  onClose,
  trendReportId,
  existingInstructionTypeIds,
  onSave,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [instructionTypes, setInstructionTypes] = useState<InstructionType[]>([]);
  const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Fetch instruction types when modal opens
  useEffect(() => {
    if (open) {
      fetchInstructionTypes();
    }
  }, [open]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedTypeId(null);
      setInstruction('');
      setError(null);
      setImageModalOpen(false);
      setSelectedImage(null);
    }
  }, [open]);

  // Scroll to top when instruction type is selected
  useEffect(() => {
    if (selectedTypeId && contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [selectedTypeId]);

  const fetchInstructionTypes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-report-instruction-types`,
        { method: 'GET' }
      );

      if (response?.ok) {
        const data = await response.json();
        setInstructionTypes(data.instructionTypes || []);
      } else {
        setError('Failed to load instruction types');
      }
    } catch (error) {
      console.error('Error fetching instruction types:', error);
      setError('An error occurred while loading instruction types');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedTypeId || !instruction.trim()) {
      setError('Please select an instruction type and enter instructions');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/trend-reports/${trendReportId}/instructions/save`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            instructionTypeId: selectedTypeId,
            instruction: instruction.trim(),
          }),
        }
      );

      if (response?.ok) {
        onSave();
        onClose();
      } else {
        const data = await response?.json();
        setError(data?.error || 'Failed to save instruction');
      }
    } catch (error) {
      console.error('Error saving instruction:', error);
      setError('An error occurred while saving the instruction');
    } finally {
      setSaving(false);
    }
  };

  // Filter out instruction types that have already been added
  const availableTypes = instructionTypes.filter(
    type => !existingInstructionTypeIds.includes(type.id)
  );

  const selectedType = instructionTypes.find(type => type.id === selectedTypeId);

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Add Special Instructions</DialogTitle>
      <DialogContent ref={contentRef}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {availableTypes.length === 0 ? (
              <Alert severity="info">
                All available instruction types have already been added to this report.
              </Alert>
            ) : (
              <>
                <Box sx={{ mb: 3, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Select Instruction Type
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedTypeId
                          ? 'Selected instruction type - enter your details below'
                          : 'Choose the type of special instruction you want to add'}
                      </Typography>
                    </Box>
                    {selectedTypeId && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setSelectedTypeId(null)}
                      >
                        Change Selection
                      </Button>
                    )}
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: selectedTypeId ? '300px' : 'auto'
                  }}>
                    <Grid container spacing={2} sx={{ maxWidth: selectedTypeId ? '600px' : '100%' }}>
                      {availableTypes
                        .filter(type => !selectedTypeId || type.id === selectedTypeId)
                        .map((type) => (
                        <Grid item xs={12} sm={selectedTypeId ? 12 : 6} key={type.id}>
                          <Card
                          sx={{
                            height: '100%',
                            border: selectedTypeId === type.id ? '2px solid' : '1px solid',
                            borderColor: selectedTypeId === type.id ? 'primary.main' : 'divider',
                            position: 'relative',
                            transition: 'all 0.2s',
                            cursor: 'pointer',
                            '&:hover': {
                              boxShadow: 4,
                              transform: 'translateY(-4px)',
                              borderColor: selectedTypeId === type.id ? 'primary.main' : 'primary.light',
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <CardActionArea
                            onClick={() => setSelectedTypeId(type.id)}
                            sx={{
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              justifyContent: 'flex-start',
                            }}
                          >
                            {type.referenceImage && (
                              <CardMedia
                                component="img"
                                height="140"
                                image={type.referenceImage}
                                alt={type.name}
                                sx={{
                                  objectFit: 'contain',
                                  bgcolor: '#f5f5f5',
                                  cursor: 'pointer',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedImage(type.referenceImage);
                                  setImageModalOpen(true);
                                }}
                              />
                            )}
                            <CardContent>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="h6" component="div" sx={{ flex: 1 }}>
                                  {type.name}
                                </Typography>
                                {selectedTypeId === type.id ? (
                                  <RadioButtonChecked color="primary" sx={{ fontSize: 28 }} />
                                ) : (
                                  <RadioButtonUnchecked sx={{ fontSize: 28, color: 'text.secondary' }} />
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {type.description}
                              </Typography>
                              {type.referenceImage && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                                  Click image to view full size
                                </Typography>
                              )}
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    ))}
                    </Grid>
                  </Box>
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', p: 3, gap: 2, bgcolor: '#f9f9f9' }}>
        {selectedType && (
          <>
            <TextField
              multiline
              fullWidth
              rows={6}
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              label="Your Instructions"
              variant="outlined"
              placeholder="Enter your specific instructions here..."
            />

            {selectedType.sampleValue && (
              <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Sample Instructions:
                </Typography>
                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                  {selectedType.sampleValue}
                </Typography>
              </Box>
            )}
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, pt: 1 }}>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving || loading || !selectedTypeId || !instruction.trim() || availableTypes.length === 0}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>

    {/* Full-size Image Modal */}
    <Modal
      open={imageModalOpen}
      onClose={() => {
        setImageModalOpen(false);
        setSelectedImage(null);
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.8)'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          maxWidth: '90vw',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 0,
          outline: 'none'
        }}
      >
        <IconButton
          onClick={() => {
            setImageModalOpen(false);
            setSelectedImage(null);
          }}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            bgcolor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 1)',
            },
            zIndex: 1
          }}
        >
          <Close />
        </IconButton>
        {selectedImage && (
          <img
            src={selectedImage}
            alt="Full size reference"
            style={{
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'block',
              objectFit: 'contain'
            }}
          />
        )}
      </Box>
    </Modal>
    </>
  );
};